import { Component, Input, OnInit, OnChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ContentApiService } from '../../../core/services/content-api.service';

@Component({
  selector: 'app-widget-renderer',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    @switch (column.widgetType) {
      <!-- IMAGE -->
      @case ('image') {
        <div class="widget-image">
          @if (config.src) {
            <img
              [src]="config.src"
              [alt]="config.alt || ''"
              [style.width]="config.width || '100%'"
              [style.height]="config.height || 'auto'"
              [style.object-fit]="config.objectFit || 'cover'"
              class="rounded-2xl"
              loading="lazy"
            />
          }
        </div>
      }

      <!-- VIDEO -->
      @case ('video') {
        <div class="widget-video rounded-2xl overflow-hidden shadow-lg shadow-surface-900/5">
          @if (config.source === 'youtube' || config.source === 'vimeo') {
            <div class="relative pb-[56.25%] h-0">
              <iframe
                [src]="config.src"
                class="absolute inset-0 w-full h-full"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
          } @else {
            <video
              [src]="config.src"
              [autoplay]="config.autoplay"
              [controls]="config.controls !== false"
              [loop]="config.loop"
              [muted]="config.muted"
              class="w-full"
            ></video>
          }
        </div>
      }

      <!-- BUTTON -->
      @case ('button') {
        <div [class]="config.fullWidth ? 'w-full' : 'inline-block'">
          <a
            [href]="config.url || '#'"
            [class]="buttonClasses"
          >
            {{ config.text || 'Learn More' }}
            @if (config.style !== 'outline') {
              <svg class="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            }
          </a>
        </div>
      }

      <!-- SPACER -->
      @case ('spacer') {
        <div [style.height.px]="config.height || 40"></div>
      }

      <!-- DIVIDER -->
      @case ('divider') {
        <div class="flex items-center py-2">
          <hr
            class="flex-1 border-0"
            [style.height.px]="config.thickness || 1"
            [style.background-color]="config.color || '#e2e5eb'"
          />
        </div>
      }

      <!-- STATIC GALLERY -->
      @case ('gallery') {
        @if (config.images?.length) {
          <div
            class="grid"
            [style.grid-template-columns]="'repeat(' + (config.columns || 3) + ', 1fr)'"
            [style.gap.px]="config.gap || 12"
          >
            @for (img of config.images; track $index) {
              <div class="overflow-hidden rounded-xl aspect-square group">
                <img [src]="img.url || img" [alt]="img.alt || ''" class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </div>
            }
          </div>
        }
      }

      <!-- CMS GALLERY -->
      @case ('cms-gallery') {
        @if (gallery) {
          <div>
            @if (gallery.title) {
              <h3 class="font-heading text-2xl font-bold text-surface-900 mb-6 tracking-[-0.02em]">{{ gallery.title }}</h3>
            }
            <div
              class="grid"
              [style.grid-template-columns]="'repeat(' + (config.columns || 3) + ', 1fr)'"
              [style.gap.px]="config.gap || 12"
            >
              @for (img of gallery.images; track $index) {
                <div class="group overflow-hidden rounded-xl aspect-square relative cursor-pointer">
                  <img [src]="img.url" [alt]="img.alt || ''" class="w-full h-full object-cover transition-all duration-500 group-hover:scale-105" loading="lazy" />
                  @if (config.showCaptions && img.alt) {
                    <div class="absolute inset-0 bg-gradient-to-t from-surface-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                      <p class="text-white text-sm font-medium p-4">{{ img.alt }}</p>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
      }

      <!-- CMS SLIDER -->
      @case ('cms-slider') {
        @if (slider) {
          <div class="relative rounded-2xl overflow-hidden shadow-2xl shadow-surface-900/10">
            <!-- Slides -->
            <div class="relative h-[400px] md:h-[540px]">
              @for (slide of slider.slides; track $index) {
                <div
                  class="absolute inset-0 transition-opacity duration-700 ease-in-out"
                  [class]="$index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'"
                >
                  <img [src]="slide.image" [alt]="slide.alt || slide.title || ''" class="w-full h-full object-cover" />
                  @if (slide.title || slide.subtitle) {
                    <div class="absolute inset-0 bg-gradient-to-t from-surface-950/80 via-surface-950/20 to-transparent flex items-end">
                      <div class="p-10 md:p-14 max-w-2xl">
                        @if (slide.title) {
                          <h3 class="font-heading text-2xl md:text-4xl font-bold text-white mb-3 tracking-[-0.02em]">{{ slide.title }}</h3>
                        }
                        @if (slide.subtitle) {
                          <p class="text-white/70 text-sm md:text-base mb-5 leading-relaxed">{{ slide.subtitle }}</p>
                        }
                        @if (slide.buttonText && slide.buttonLink) {
                          <a [href]="slide.buttonLink" class="group inline-flex items-center gap-2.5 px-6 py-3 bg-white text-surface-900 font-semibold text-sm rounded-lg hover:bg-primary-50 transition-all duration-200">
                            {{ slide.buttonText }}
                            <svg class="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                          </a>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>

            <!-- Arrows -->
            @if (slider.showArrows && slider.slides.length > 1) {
              <button (click)="prevSlide()" class="absolute left-5 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-11 h-11 rounded-full bg-white/10 backdrop-blur-md text-white border border-white/10 hover:bg-white/20 transition-all duration-200">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" /></svg>
              </button>
              <button (click)="nextSlide()" class="absolute right-5 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-11 h-11 rounded-full bg-white/10 backdrop-blur-md text-white border border-white/10 hover:bg-white/20 transition-all duration-200">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
            }

            <!-- Dots -->
            @if (slider.showDots && slider.slides.length > 1) {
              <div class="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
                @for (slide of slider.slides; track $index) {
                  <button
                    (click)="currentSlide = $index"
                    class="h-1.5 rounded-full transition-all duration-300"
                    [class]="$index === currentSlide ? 'bg-white w-8' : 'bg-white/40 w-1.5 hover:bg-white/60'"
                  ></button>
                }
              </div>
            }
          </div>
        }
      }

      <!-- CMS PROJECT -->
      @case ('cms-project') {
        @if (project) {
          <div class="group rounded-2xl overflow-hidden border border-surface-200/60 bg-white shadow-sm hover:shadow-xl hover:shadow-surface-900/5 transition-all duration-300">
            @if (project.coverImage) {
              <div class="aspect-video overflow-hidden">
                <img [src]="project.coverImage" [alt]="project.name" class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </div>
            }
            <div class="p-7">
              <h3 class="font-heading text-xl font-bold text-surface-900 mb-2 tracking-[-0.01em]">{{ project.name }}</h3>
              @if (project.description) {
                <p class="text-surface-500 text-sm leading-relaxed mb-5">{{ project.description }}</p>
              }
              @if (project.tags?.length) {
                <div class="flex flex-wrap gap-2">
                  @for (tag of project.tags; track tag) {
                    <span class="px-3 py-1 text-xs font-medium bg-surface-100 text-surface-600 rounded-md">{{ tag }}</span>
                  }
                </div>
              }
            </div>
          </div>
        }
      }

      <!-- CMS DIVISIONS -->
      @case ('cms-divisions') {
        @if (division) {
          <div class="rounded-2xl border border-surface-200/60 bg-white shadow-sm overflow-hidden hover:shadow-xl hover:shadow-surface-900/5 transition-all duration-300">
            @if (config.showFeaturedImage && division.featuredImage) {
              <div class="aspect-video overflow-hidden">
                <img [src]="division.featuredImage" [alt]="division.name" class="w-full h-full object-cover" loading="lazy" />
              </div>
            }
            <div class="p-7">
              <div class="flex items-start justify-between mb-4">
                <div class="flex items-center gap-3">
                  @if (division.logo) {
                    <img [src]="division.logo" [alt]="division.name" class="w-11 h-11 rounded-xl object-contain border border-surface-100 p-1" />
                  }
                  <h3 class="font-heading text-xl font-bold text-surface-900 tracking-[-0.01em]">{{ division.name }}</h3>
                </div>
              </div>
              @if (division.description) {
                <p class="text-surface-500 text-sm leading-relaxed mb-5">{{ division.description }}</p>
              }
              @if (config.showContact) {
                <div class="space-y-2.5 mb-5">
                  @if (division.contactEmail) {
                    <p class="flex items-center gap-2.5 text-sm text-surface-500">
                      <svg class="w-4 h-4 shrink-0 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                      {{ division.contactEmail }}
                    </p>
                  }
                  @if (division.phones?.length) {
                    <p class="flex items-center gap-2.5 text-sm text-surface-500">
                      <svg class="w-4 h-4 shrink-0 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                      {{ division.phones[0].number }}
                    </p>
                  }
                </div>
              }
              @if (config.showLocations && divisionLocations.length) {
                <div class="border-t border-surface-100 pt-5 mt-5">
                  <h4 class="text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-surface-400 mb-3">Locations</h4>
                  <div class="space-y-2">
                    @for (loc of divisionLocations; track loc._id) {
                      <div class="flex items-start gap-2.5 text-sm text-surface-500">
                        <svg class="w-4 h-4 shrink-0 mt-0.5 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>
                        <span>{{ loc.name }} &mdash; {{ loc.address?.street }}, {{ loc.address?.city }}</span>
                      </div>
                    }
                  </div>
                </div>
              }
              @if (config.linkToWebsite && divisionWebsiteUrl) {
                <a [href]="divisionWebsiteUrl" class="group inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 mt-5 transition-colors">
                  Visit Website
                  <svg class="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </a>
              }
            </div>
          </div>
        }
      }

      <!-- CMS FORM -->
      @case ('cms-form') {
        @if (form) {
          <div class="rounded-2xl border border-surface-200/60 bg-white shadow-sm p-8 md:p-10">
            <h3 class="font-heading text-2xl font-bold text-surface-900 mb-2 tracking-[-0.02em]">{{ form.name }}</h3>
            @if (form.description) {
              <p class="text-surface-500 text-sm mb-8 leading-relaxed">{{ form.description }}</p>
            }

            @if (!formSubmitted) {
              <form [formGroup]="formGroup" (ngSubmit)="onSubmitForm()" class="space-y-6">
                @for (field of form.fields; track field.name) {
                  <div>
                    <label [for]="field.name" class="block text-sm font-medium text-surface-700 mb-2">
                      {{ field.label }}
                      @if (field.required) {
                        <span class="text-red-500 ml-0.5">*</span>
                      }
                    </label>

                    @switch (field.type) {
                      @case ('textarea') {
                        <textarea
                          [id]="field.name"
                          [formControlName]="field.name"
                          [placeholder]="field.placeholder || ''"
                          rows="4"
                          class="w-full rounded-xl border border-surface-200 bg-white px-4 py-3 text-sm text-surface-900 placeholder:text-surface-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all duration-200"
                        ></textarea>
                      }
                      @case ('select') {
                        <select
                          [id]="field.name"
                          [formControlName]="field.name"
                          class="w-full rounded-xl border border-surface-200 bg-white px-4 py-3 text-sm text-surface-900 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all duration-200"
                        >
                          <option value="">Select...</option>
                          @for (opt of field.options; track opt.value) {
                            <option [value]="opt.value">{{ opt.label }}</option>
                          }
                        </select>
                      }
                      @case ('checkbox') {
                        <label class="flex items-center gap-3 cursor-pointer">
                          <input type="checkbox" [formControlName]="field.name" class="w-4 h-4 rounded border-surface-300 text-primary-600 focus:ring-primary-500" />
                          <span class="text-sm text-surface-600">{{ field.placeholder || field.label }}</span>
                        </label>
                      }
                      @case ('radio') {
                        <div class="space-y-2.5">
                          @for (opt of field.options; track opt.value) {
                            <label class="flex items-center gap-3 cursor-pointer">
                              <input type="radio" [formControlName]="field.name" [value]="opt.value" class="w-4 h-4 border-surface-300 text-primary-600 focus:ring-primary-500" />
                              <span class="text-sm text-surface-600">{{ opt.label }}</span>
                            </label>
                          }
                        </div>
                      }
                      @default {
                        <input
                          [id]="field.name"
                          [formControlName]="field.name"
                          [placeholder]="field.placeholder || ''"
                          type="text"
                          class="w-full rounded-xl border border-surface-200 bg-white px-4 py-3 text-sm text-surface-900 placeholder:text-surface-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all duration-200"
                        />
                      }
                    }
                  </div>
                }

                <button
                  type="submit"
                  [disabled]="formSubmitting"
                  class="group w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-3 bg-surface-900 text-white font-semibold text-sm rounded-xl hover:bg-surface-800 focus:ring-4 focus:ring-surface-200 transition-all duration-200 disabled:opacity-50"
                >
                  @if (formSubmitting) {
                    <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Submitting...
                  } @else {
                    Submit
                    <svg class="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                  }
                </button>
              </form>
            } @else {
              <div class="text-center py-10">
                <div class="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-50 text-green-600 mb-5">
                  <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                </div>
                <h4 class="font-heading text-xl font-bold text-surface-900 mb-2">Thank you!</h4>
                <p class="text-surface-500 text-sm">Your submission has been received. We'll be in touch shortly.</p>
              </div>
            }
          </div>
        }
      }

      <!-- HTML -->
      @case ('html') {
        @if (column.content) {
          <div class="prose-content" [innerHTML]="column.content"></div>
        }
      }

      <!-- UNKNOWN -->
      @default {
        <!-- Unsupported widget type: {{ column.widgetType }} -->
      }
    }
  `,
})
export class WidgetRendererComponent implements OnInit, OnChanges {
  @Input() column: any;

  config: any = {};

  // CMS data
  gallery: any = null;
  slider: any = null;
  project: any = null;
  division: any = null;
  divisionLocations: any[] = [];
  divisionWebsiteUrl: string | null = null;
  form: any = null;
  formGroup!: FormGroup;
  formSubmitting = false;
  formSubmitted = false;

  // Slider state
  currentSlide = 0;
  private slideInterval: any;

  constructor(
    private contentApi: ContentApiService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.config = this.column?.widgetConfig || {};
    this.loadCmsData();
  }

  ngOnChanges(): void {
    this.config = this.column?.widgetConfig || {};
  }

  get buttonClasses(): string {
    const style = this.config.style || 'primary';
    const size = this.config.size || 'md';
    const base = 'group inline-flex items-center justify-center gap-2.5 font-semibold rounded-xl transition-all duration-200';
    const sizes: Record<string, string> = {
      sm: 'px-5 py-2.5 text-sm',
      md: 'px-7 py-3 text-sm',
      lg: 'px-9 py-4 text-base',
    };
    const styles: Record<string, string> = {
      primary: 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm hover:shadow-lg hover:shadow-primary-600/20',
      secondary: 'bg-surface-900 text-white hover:bg-surface-800 shadow-sm hover:shadow-lg hover:shadow-surface-900/20',
      outline: 'border border-surface-200 text-surface-700 hover:border-surface-900 hover:text-surface-900 hover:bg-surface-50',
    };
    return `${base} ${sizes[size] || sizes['md']} ${styles[style] || styles['primary']} ${this.config.fullWidth ? 'w-full' : ''}`;
  }

  private loadCmsData(): void {
    const type = this.column?.widgetType;
    if (!type) return;

    if (type === 'cms-gallery' && this.config.galleryId) {
      this.contentApi.getGalleryById(this.config.galleryId).subscribe({
        next: g => { this.gallery = g; this.cdr.detectChanges(); },
        error: () => {}
      });
    }

    if (type === 'cms-slider' && this.config.sliderId) {
      this.contentApi.getSliderById(this.config.sliderId).subscribe({
        next: s => {
          this.slider = s;
          this.cdr.detectChanges();
          if (s.autoplay && s.slides?.length > 1) {
            this.startAutoplay(s.speed || 5000);
          }
        },
        error: () => {}
      });
    }

    if (type === 'cms-project' && this.config.projectId) {
      this.contentApi.getProjects().subscribe({
        next: (projects: any[]) => {
          this.project = projects.find((p: any) => p._id === this.config.projectId);
          this.cdr.detectChanges();
        },
        error: () => {}
      });
    }

    if (type === 'cms-divisions' && this.config.divisionId) {
      this.contentApi.getDivisionById(this.config.divisionId).subscribe({
        next: d => {
          this.division = d;
          if (d.websiteUrlType === 'external' && d.websiteUrl) {
            this.divisionWebsiteUrl = d.websiteUrl;
          } else if (d.websiteUrlType === 'cms' && d.websiteId) {
            this.divisionWebsiteUrl = `/website/${d.websiteId}`;
          }
          this.cdr.detectChanges();
          if (this.config.showLocations) {
            this.contentApi.getDivisionLocations(d._id).subscribe({
              next: locs => { this.divisionLocations = locs; this.cdr.detectChanges(); },
              error: () => {}
            });
          }
        },
        error: () => {}
      });
    }

    if (type === 'cms-form' && this.config.formId) {
      this.contentApi.getFormById(this.config.formId).subscribe({
        next: f => {
          this.form = f;
          this.buildFormGroup(f.fields || []);
          this.cdr.detectChanges();
        },
        error: () => {}
      });
    }
  }

  private buildFormGroup(fields: any[]): void {
    const group: Record<string, any> = {};
    for (const field of fields) {
      group[field.name] = [field.type === 'checkbox' ? false : ''];
    }
    this.formGroup = this.fb.group(group);
  }

  onSubmitForm(): void {
    if (!this.form || this.formSubmitting) return;
    this.formSubmitting = true;
    this.contentApi.submitForm(this.form._id, this.formGroup.value).subscribe({
      next: () => {
        this.formSubmitting = false;
        this.formSubmitted = true;
      },
      error: () => {
        this.formSubmitting = false;
      }
    });
  }

  // Slider navigation
  private startAutoplay(speed: number): void {
    this.slideInterval = setInterval(() => this.nextSlide(), speed);
  }

  nextSlide(): void {
    if (!this.slider?.slides?.length) return;
    this.currentSlide = (this.currentSlide + 1) % this.slider.slides.length;
  }

  prevSlide(): void {
    if (!this.slider?.slides?.length) return;
    this.currentSlide = (this.currentSlide - 1 + this.slider.slides.length) % this.slider.slides.length;
  }

  ngOnDestroy(): void {
    if (this.slideInterval) clearInterval(this.slideInterval);
  }
}
